/**
 * Delete-account confirmation (cloud only, opened from AccountMenu). Irreversible and total, so the
 * button stays disabled until the user types the confirmation word. A 'reauth' answer means the
 * provider wants a recent sign-in first: the dialog says so and offers to sign out (the user signs
 * back in and deletes within the window).
 */
import { useState } from 'react';
import { Button } from '@onestone/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@onestone/ui/dialog';
import { Input } from '@onestone/ui/input';

/** What the user types to enable the delete button. */
export const DELETE_CONFIRMATION = 'DELETE';

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  deleteAccount: () => Promise<'deleted' | 'reauth'>;
  signOut: () => Promise<void>;
}

export function DeleteAccountDialog({ open, onOpenChange, email, deleteAccount, signOut }: DeleteAccountDialogProps) {
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (next: boolean) => {
    if (pending) return;
    if (!next) {
      setTyped('');
      setNeedsReauth(false);
      setError(null);
    }
    onOpenChange(next);
  };

  const handleDelete = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await deleteAccount();
      if (result === 'reauth') setNeedsReauth(true);
      // 'deleted': the host has already left the app (its sign-in screen).
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your account could not be deleted. Nothing was removed.');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete your account?</DialogTitle>
          <DialogDescription>
            This permanently deletes <strong className="text-foreground">{email}</strong> and everything stored with it
            on OneStone Cloud: conversations, knowledge, projects, calendar, contacts, routines, settings and saved
            keys. It can&apos;t be undone. Your desktop app&apos;s own data is not affected.
          </DialogDescription>
        </DialogHeader>
        {needsReauth ? (
          <p className="text-sm text-muted-foreground" role="status">
            For your security, deleting an account needs a recent sign-in. Sign out, sign back in, then delete it within
            24 hours.
          </p>
        ) : (
          <label className="grid gap-1.5 text-sm">
            <span>
              Type <span className="font-mono font-semibold">{DELETE_CONFIRMATION}</span> to confirm
            </span>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={pending}
              aria-label={`Type ${DELETE_CONFIRMATION} to confirm`}
            />
          </label>
        )}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => close(false)} disabled={pending}>
            Cancel
          </Button>
          {needsReauth ? (
            <Button size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          ) : (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void handleDelete()}
              disabled={pending || typed.trim() !== DELETE_CONFIRMATION}
            >
              {pending ? 'Deleting…' : 'Delete account'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
